import normalizeWhitespaceText from '@/lib/normalizeWhitespaceText.js';

/**
 * Builds the grouped result list behind the sidebar search box: the chats, automations and
 * analytics pages whose name contains the typed text, each carrying the path that opens it.
 * Groups come back in a fixed order (Chats, Automations, Pages) and empty groups are dropped.
 */
const MAX_ITEMS_PER_GROUP = 8;

const buildSidebarSearchResults = ({ query = '', threads = [], schedules = [], triggers = [], workflows = [] } = {}) => {
  const normalizedQuery = normalizeWhitespaceText(query).toLocaleLowerCase();

  if (!normalizedQuery) {
    return [];
  }

  const collectMatches = (items) => {
    return items.filter((item) => item?.id && normalizeWhitespaceText(item.label).toLocaleLowerCase().includes(normalizedQuery)).slice(0, MAX_ITEMS_PER_GROUP);
  };

  /* Thread records from the API carry `threadId`, not `id`. */
  const chatItems = collectMatches(
    threads.map((thread) => ({
      key: `thread:${thread?.threadId}`,
      kind: 'thread',
      id: thread?.threadId,
      label: thread?.title || 'New thread',
      href: `/threads/${encodeURIComponent(String(thread?.threadId || ''))}`
    }))
  );

  const automationItems = collectMatches([
    ...schedules.map((schedule) => ({
      key: `schedule:${schedule?.id}`,
      kind: 'schedule',
      id: schedule?.id,
      label: schedule?.title || 'Untitled schedule',
      href: `/automations/schedules/${encodeURIComponent(String(schedule?.id || ''))}`
    })),
    ...triggers.map((trigger) => ({
      key: `trigger:${trigger?.id}`,
      kind: 'trigger',
      id: trigger?.id,
      label: trigger?.title || 'Untitled trigger',
      href: `/automations/triggers/${encodeURIComponent(String(trigger?.id || ''))}`
    })),
    /* A workflow still being planned has no workflow record yet; its authoring thread is the
       only place to open it, mirroring how the Automate sidebar treats planning rows. */
    ...workflows.map((workflow) => ({
      key: `workflow:${workflow?.id}`,
      kind: 'workflow',
      id: workflow?.id,
      label: workflow?.title || 'Untitled workflow',
      href: workflow?.isPlanning ? `/automations/workflows/${encodeURIComponent(String(workflow?.threadId || workflow?.id || ''))}/conversation` : `/automations/workflows/${encodeURIComponent(String(workflow?.id || ''))}`
    }))
  ]);

  return [
    { id: 'chats', label: 'Chats', items: chatItems },
    { id: 'automations', label: 'Automations', items: automationItems }
  ].filter((group) => group.items.length > 0);
};

export default buildSidebarSearchResults;
